import React, { useCallback, useMemo, useRef, useState } from 'react'
import { navigate, RouteComponentProps } from '@reach/router'
import { Box, HStack, Image, Text, VStack } from '@chakra-ui/react'

import { Loading } from 'components/Common/Loading'
import { NoData } from 'components/Common/NoData'
import { Private } from 'components/Common/Private'

import { useQuery, useMutation } from '@apollo/client'
import { ENQUETE, EnqueteData, EnqueteVars } from 'graphql/apollo/query/enquete'
import {
  ENQUETE_STATS,
  EnqueteStatsData,
  EnqueteStatsVars,
} from 'graphql/apollo/query/enqueteStats'
import {
  ANSWER_ENQUETE,
  AnswerEnqueteData,
  AnswerEnqueteVars,
} from 'graphql/apollo/mutation/answer/answerEnquete'
import {
  getAnsweredEnquete,
  getEmbedHeight,
  isAnsweredEnquete,
  saveAnsweredEnquete,
} from 'utils/answeredEnquete'
import { EmbedHorizontalLine } from 'components/AnswerEnquete/EmbedHorizontalLine'

type Props = RouteComponentProps<{
  enqueteId: string
  isPreview?: boolean
}>

export const EmbedAnswerEnquete: React.FC<Props> = ({
  enqueteId,
  isPreview = false,
}) => {
  const elm = useRef<HTMLInputElement>(null)
  const [answered, setAnswered] = useState<string>(
    useMemo(() => {
      if (!isPreview && enqueteId && isAnsweredEnquete(enqueteId)) {
        return getAnsweredEnquete(enqueteId)
      }
      return ''
    }, [isPreview, enqueteId])
  )
  const [isSkippedAnswer, setIsSkippedAnswer] = useState(false)
  const [answerEnquete] = useMutation<AnswerEnqueteData, AnswerEnqueteVars>(
    ANSWER_ENQUETE
  )
  const {
    loading: enqueteLoading,
    error: enqueteError,
    data: enqueteData,
  } = useQuery<EnqueteData, EnqueteVars>(ENQUETE, {
    variables: {
      id: enqueteId!,
    },
    context: {
      noAuthorizationHeader: !isPreview,
    },
  })
  const {
    loading: enqueteStatsLoading,
    data: enqueteStatsData,
    refetch,
  } = useQuery<EnqueteStatsData, EnqueteStatsVars>(ENQUETE_STATS, {
    variables: {
      enqueteId: enqueteId!,
    },
  })
  const handleSelectedChoice = useCallback(
    async (selectedChoice) => {
      if (!isPreview && enqueteId && !isAnsweredEnquete(enqueteId)) {
        await answerEnquete({
          variables: {
            input: {
              answer: selectedChoice,
              enqueteId: enqueteId,
            },
          },
        })
        saveAnsweredEnquete(enqueteId, selectedChoice)
        await refetch()
      }
      setAnswered(selectedChoice)
    },
    [answerEnquete, enqueteId, isPreview, refetch]
  )
  const enquete = enqueteData?.enquete
  const isExpired = useMemo(() => {
    if (!enquete?.deadline) {
      return false
    }
    return new Date() >= new Date(enquete.deadline)
  }, [enquete])

  const canAnswer = !isExpired && !answered && !isSkippedAnswer

  if (enqueteLoading || enqueteStatsLoading) return <Loading />
  if (enqueteError) navigate('/404NotFound')
  if (!enqueteData || !enquete || !enqueteStatsData) return <NoData />
  if (!isPreview && !enqueteData.enquete.published) {
    return <Private category="アンケート" />
  }

  const setEmbedWindowHeight = () => {
    if (!elm.current) {
      return
    }
    const { clientHeight } = elm.current
    // iframe埋込時に表示される枠の高さを最後にプラスする
    window.parent.postMessage([clientHeight + 21], '*')
  }

  return (
    <VStack w="100%" onLoad={setEmbedWindowHeight} ref={elm}>
      <Box w="100%">
        <Box h="100%" position="relative" borderRadius="16px" overflow="hidden">
          <Image
            position="absolute"
            w="100%"
            h="100%"
            objectFit="cover"
            zIndex="-1"
            filter="brightness(65%)"
            src={enquete.imageUrl || `/images/quiz_bg.png`}
            alt={enquete.title}
          />
          <VStack
            w="100%"
            h="100%"
            minHeight={getEmbedHeight(enquete)}
            spacing={4}
            justify="center"
            position="relative"
            color="white"
            p={{ base: '10px 15px', sm: '15px 30px' }}
          >
            <Text
              fontSize={{ base: '18px', sm: '22px' }}
              mt="16px"
              mb=".4rem"
              display="flex"
              fontWeight="700"
              textShadow="0 0 5px rgba(0, 0, 0, 0.5)"
              _before={{
                content: '"Q"',
                textShadow: 'none',
                w: '1.8em',
                minWidth: '1.8em',
                h: '1.8em',
                borderRadius: '50%',
                fontSize: '70%',
                fontWeight: '800',
                mr: '.3em',
                mt: '.25em',
                bg: '#ffaf00',
                color: '#fff',
                textAlign: 'center',
              }}
            >
              {enquete.question}
            </Text>

            {enqueteStatsData.enqueteStats.stats.map((stats, idx) => (
              <EmbedHorizontalLine
                key={idx}
                enquete={enquete}
                stats={stats}
                onSelectedChoice={handleSelectedChoice}
                canAnswer={canAnswer}
                answered={answered}
              />
            ))}
            <HStack w="100%" h="20px" justify="right">
              <Text
                fontSize="sm"
                onClick={() => {
                  setIsSkippedAnswer(true)
                }}
                textDecoration="underline"
                cursor="pointer"
                textShadow="0 0 5px rgba(0, 0, 0, 0.5)"
                display={canAnswer ? 'block' : 'none'}
              >
                投票せずに結果を見る
              </Text>
            </HStack>
          </VStack>
        </Box>
      </Box>
    </VStack>
  )
}
